import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Check, Loader2 } from "lucide-react";
import { usePricing, productOf, fmt, checkoutHref, bookingHref } from "../lib/catalog";
import CtaLink, { btnOrange, btnPrimary, btnOutline } from "../components/CtaLink";
import site from "../content/site.json";

function PriceOptions({ p, booking }) {
  if (p.type === "one_time") {
    return <div className="mt-8"><span className="font-heading text-5xl font-bold">{fmt(p.price)}</span><span className="text-slate-400 ml-2">one-time</span><p className="text-indigo-200 mt-2">{p.credit_note} {p.delivery}</p>
      <CtaLink to={checkoutHref(p)} className={`${btnOrange} mt-8 h-14 px-8 text-base`} testid="product-cta">{p.cta}</CtaLink></div>;
  }
  if (p.type === "custom_quote") {
    return <div className="mt-8"><span className="font-heading text-5xl font-bold">From {fmt(p.setup_price)}</span><span className="text-slate-400 ml-2">setup + {fmt(p.monthly_price)}/month</span>
      <div><CtaLink to={bookingHref(booking.strategy_call_url)} className={`${btnOrange} mt-8 h-14 px-8 text-base`} testid="product-cta">Book a Strategy Call</CtaLink></div></div>;
  }
  return (
    <div className="mt-8 grid sm:grid-cols-3 gap-3" data-testid="product-options">
      {p.options.map((o) => (
        <div key={o.id} className="rounded-xl border border-white/15 bg-white/5 p-5 flex flex-col">
          <p className="text-xs font-mono uppercase tracking-wide text-indigo-300">{o.label}</p>
          <p className="font-heading text-2xl font-bold mt-2">{o.id === "plan" ? `${o.installments} × ${fmt(o.installment_amount)}` : `${fmt(o.setup_price)} setup`}</p>
          <p className="text-sm text-slate-300 mt-1 flex-1">+ {fmt(o.recurring.amount)}/{o.recurring.interval} monitoring</p>
          <CtaLink to={checkoutHref(p, o.id)} className={`${btnOrange} mt-4 w-full`} testid={`product-cta-${o.id}`}>{p.cta}</CtaLink>
        </div>
      ))}
      {p.code === "agentic_ready" && <CtaLink to={bookingHref(booking.score_review_url)} className={`${btnOutline} sm:col-span-3`} testid="product-score-review">Book a 15-min Score Review</CtaLink>}
    </div>
  );
}

export default function Product({ code }) {
  const { data } = usePricing();
  const p = productOf(data, code);
  if (!p) return <div className="py-40 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  return (
    <div>
      <section className="bg-gradient-to-b from-slate-950 to-indigo-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          {p.badge && <span className="inline-block mb-4 px-3 py-1 rounded-full bg-indigo-600 text-xs font-mono font-semibold">{p.badge}</span>}
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" data-testid="product-title">{p.name}</h1>
          <p className="mt-4 text-xl text-indigo-200 font-heading">{p.promise}</p>
          <PriceOptions p={p} booking={data.booking} />
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">{p.type === "one_time" ? "What's included" : "What's implemented"}</h2>
          <ul className="mt-5 space-y-3">{p.features.map((f, i) => <li key={i} className="flex items-start gap-2.5 text-slate-700"><Check className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />{f}</li>)}</ul>
        </div>
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Who it's for</h2>
          <div className="mt-5 flex flex-wrap gap-2">{p.ideal_for.map((x) => <span key={x} className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-sm">{x}</span>)}</div>
          {p.not_guaranteed && <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50/50 p-5"><h3 className="font-heading font-semibold text-slate-900">What is not guaranteed</h3><p className="text-sm text-slate-600 mt-2">{p.not_guaranteed}</p></div>}
        </div>
      </section>
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-heading text-2xl font-bold text-slate-900 text-center">Frequently asked</h2>
          <Accordion type="single" collapsible className="mt-6">
            {(site.product_faq[code] || []).map(([q, a], i) => (
              <AccordionItem key={i} value={`i${i}`} data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-semibold text-slate-800">{q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-10"><CtaLink to={p.type === "custom_quote" ? bookingHref(data.booking.strategy_call_url) : checkoutHref(p)} className={btnPrimary} testid="product-bottom-cta">{p.type === "custom_quote" ? "Book a Strategy Call" : p.cta}</CtaLink></div>
        </div>
      </section>
    </div>
  );
}
